/** Renders metadata-defined segments; loading feedback appears only for sustained requests. */
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { DecimalPipe, DOCUMENT } from '@angular/common';
import { MetadataEntitySegmentFilterDisplayType } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { EntityBoardStore } from '../../state';
import { boardSegmentTilesBridgeStyles, boardSegmentTilesStyleId } from './board.segment.tiles.theme';

@Component({
  selector: 'board-segment-tiles',
  templateUrl: './board.segment.tiles.component.html',
  styleUrls: ['./board.segment.tiles.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DecimalPipe],
  host: {
    '[attr.data-size]': '$compact() ? "compact" : "default"',
  },
})
/** The board header's segment pills or tiles, preserving count labels while their query refetches. */
export class BoardSegmentTilesComponent {

  protected readonly store = inject(EntityBoardStore);

  private readonly document = inject(DOCUMENT);

  /** Smaller tiles/pills on an embedded board (Job Items, Tasks). Not a third display type. */
  protected readonly $compact = computed(() => this.store.$contextIsEmbedded());

  protected readonly $displayType = computed<MetadataEntitySegmentFilterDisplayType>(() => this.store.$segmentFilterConfig()?.display?.type ?? 'pills');

  protected readonly $showLoading = signal(false);

  public constructor() {

    DesignSystem.Mount(this.document, boardSegmentTilesStyleId, boardSegmentTilesBridgeStyles);
    effect(onCleanup => {
      this.$showLoading.set(false);
      if (!this.store.$segmentCountsLoading()) return;

      // Both settling and destruction cancel the timer, so a quick refresh never flashes later.
      const timeout = setTimeout(() => this.$showLoading.set(true), 500);
      onCleanup(() => clearTimeout(timeout));
    });
  }

  protected onSelect(id: string): void {

    this.store.selectSegment(id);
  }

  /** Explains server-capped counts without presenting an exact-looking limit as a complete total. */
  protected countTooltip(id: string): string | null {

    const limit = this.store.$segmentCountLimit();
    if (!this.store.$segmentCountsReady() || !limit || (this.store.$segmentCounts()[id] ?? 0) < limit) return null;
    return `Count limited to ${limit.toLocaleString('en-AU')}. There may be more matching records. Narrow your filters to see a smaller result set.`;
  }
}
